import type { ReactNode } from "react";
import { highlightRanges } from "./hosOverlayText";
import "../../../styles/hos/components/overlays.css";

/** `text` with the words of `query` wrapped in <mark> (accent- and case-insensitive). */
export function HosHighlight({ text, query }: { text: string; query: string }) {
  const ranges = highlightRanges(text, query);
  if (!ranges.length) return <>{text}</>;
  const parts: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([start, end], index) => {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(<mark key={index} className="hos-ov__mark">{text.slice(start, end)}</mark>);
    cursor = end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

type StateTone = "neutral" | "warn" | "bad";

/**
 * Calm full-area state (empty, no access, error): one muted icon, a title, one short line,
 * optional technical detail and at most one primary action. Never a coloured tile.
 */
export function HosOvState({ icon, tone = "neutral", title, text, detail, detailLabel, actions, role, compact = false }: {
  icon?: ReactNode;
  tone?: StateTone;
  title: ReactNode;
  text?: ReactNode;
  /** Raw error text, shown collapsed under `detailLabel`. */
  detail?: string;
  detailLabel?: string;
  actions?: ReactNode;
  role?: "alert" | "status";
  compact?: boolean;
}) {
  return <div className={`hos-ov-state hos-ov-state--${tone}${compact ? " hos-ov-state--compact" : ""}`} role={role}>
    {icon ? <span className="hos-ov-state__icon" aria-hidden="true">{icon}</span> : null}
    <strong className="hos-ov-state__title">{title}</strong>
    {text ? <p className="hos-ov-state__text">{text}</p> : null}
    {detail ? <details className="hos-ov-state__detail">
      <summary>{detailLabel}</summary>
      <code>{detail}</code>
    </details> : null}
    {actions ? <div className="hos-ov-state__actions">{actions}</div> : null}
  </div>;
}
